import {
	MessageCircle,
	Brain,
	Target,
	Zap,
	Database,
	CheckCircle,
	Globe,
	Search,
	Mail,
	ExternalLink
} from 'lucide-react';
import Image from 'next/image';

export function SolutionSection() {
	return (
		<section className="py-20 px-6 lg:px-8 bg-white">
			<div className="mx-auto max-w-6xl">
				<div className="text-center mb-16">
					<h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
						How NOS Leads Solves It
					</h2>
					<p className="mt-4 text-lg text-gray-600">
						Three core capabilities that make lead generation actually work in 2025
					</p>
				</div>

				{/* Solution 1: AI Web Navigation */}
				<div className="mb-20">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
						<div>
							<div className="flex items-center space-x-3 mb-6">
								<div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
									<Globe className="w-6 h-6 text-blue-600" />
								</div>
								<h3 className="text-2xl font-bold text-gray-900">
									AI Agents Navigate Web 1000x Faster
								</h3>
							</div>
							<p className="text-lg text-gray-600 mb-6">
								While others are stuck with overused databases, our AI agents scour
								the entire internet to find fresh leads from unique sources that
								actually convert.
							</p>
							<ul className="space-y-3 text-gray-600">
								<li className="flex items-center space-x-3">
									<CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
									<span>GitHub commits & activity tracking</span>
								</li>
								<li className="flex items-center space-x-3">
									<CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
									<span>Social media engagement analysis</span>
								</li>
								<li className="flex items-center space-x-3">
									<CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
									<span>Conference speakers & industry events</span>
								</li>
								<li className="flex items-center space-x-3">
									<CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
									<span>Company blogs & thought leadership</span>
								</li>
								<li className="flex items-center space-x-3">
									<CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
									<span>...and many more</span>
								</li>
							</ul>
						</div>

						{/* AI Web Navigation Mockup */}
						<div className="bg-gradient-to-br from-blue-50 to-purple-50 p-6 rounded-3xl">
							<div className="bg-white rounded-2xl p-6 shadow-sm">
								<div className="flex items-center space-x-3 mb-4">
									<div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
										<Globe className="w-4 h-4 text-white" />
									</div>
									<span className="font-medium text-gray-900">
										AI Web Navigator
									</span>
								</div>
								<div className="space-y-3 text-sm">
									<div className="flex items-center space-x-2 text-gray-600">
										<Search className="w-4 h-4" />
										<span>Scanning GitHub for React commits...</span>
									</div>
									<div className="bg-gray-50 p-3 rounded-lg">
										<div className="flex items-center space-x-2 mb-2">
											<div className="w-4 h-4 bg-purple-500 rounded"></div>
											<span className="font-medium">github.com/techcorp</span>
										</div>
										<p className="text-xs text-gray-600">
											Found: John Smith committed React hooks optimization
										</p>
									</div>
									<div className="bg-gray-50 p-3 rounded-lg">
										<div className="flex items-center space-x-2 mb-2">
											<div className="w-4 h-4 bg-blue-500 rounded"></div>
											<span className="font-medium">
												linkedin.com/in/sarah-chen
											</span>
										</div>
										<p className="text-xs text-gray-600">
											Found: CTO posting about React migration challenges
										</p>
									</div>
									<div className="flex items-center space-x-2 text-green-600 font-medium">
										<CheckCircle className="w-4 h-4" />
										<span>47 unique leads discovered in 12 seconds</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Solution 2: Real-time Contact Finding */}
				<div className="mb-20">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
						{/* Contact Finding Mockup - Clay-style Spreadsheet */}
						<div className="lg:order-1 bg-gradient-to-br from-green-50 to-teal-50 p-6 rounded-3xl">
							<div className="bg-white rounded-2xl p-4 shadow-sm">
								<div className="flex items-center space-x-3 mb-4">
									<div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
										<Mail className="w-4 h-4 text-white" />
									</div>
									<span className="font-medium text-gray-900">
										Live Spreadsheet Enrichment
									</span>
								</div>

								{/* Clay-style spreadsheet */}
								<div className="border rounded-lg overflow-hidden bg-white">
									{/* Header */}
									<div className="bg-gray-50 border-b">
										<div className="flex text-xs font-medium text-gray-600 p-3">
											<div className="w-20">Name</div>
											<div className="w-20">Company</div>
											<div className="w-16">Title</div>
											<div className="flex-1">Email</div>
											<div className="w-16 text-center">Status</div>
										</div>
									</div>

									{/* Row 1 - Complete */}
									<div className="border-b hover:bg-gray-50">
										<div className="flex text-xs p-3 items-center">
											<div className="w-20 font-medium text-gray-900 truncate">
												John Smith
											</div>
											<div className="w-20 text-gray-600 truncate">
												TechCorp
											</div>
											<div className="w-16 text-gray-600 truncate">CTO</div>
											<div className="flex-1 text-gray-600 truncate">
												john@techcorp.io
											</div>
											<div className="w-16 text-center">
												<span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">
													✓
												</span>
											</div>
										</div>
									</div>

									{/* Row 2 - Enriching */}
									<div className="border-b hover:bg-gray-50">
										<div className="flex text-xs p-3 items-center">
											<div className="w-20 font-medium text-gray-900 truncate">
												Sarah Chen
											</div>
											<div className="w-20 text-gray-600 truncate">
												DataFlow
											</div>
											<div className="w-16 text-gray-600 truncate">
												VP Eng
											</div>
											<div className="flex-1">
												<div className="flex items-center space-x-1">
													<div className="w-2 h-2 border border-blue-500 border-t-transparent rounded-full animate-spin"></div>
													<span className="text-blue-600 text-xs">
														Finding...
													</span>
												</div>
											</div>
											<div className="w-16 text-center">
												<span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">
													🔄
												</span>
											</div>
										</div>
									</div>

									{/* Row 3 - Verifying */}
									<div className="border-b hover:bg-gray-50">
										<div className="flex text-xs p-3 items-center">
											<div className="w-20 font-medium text-gray-900 truncate">
												Mike Johnson
											</div>
											<div className="w-20 text-gray-600 truncate">
												CloudStart
											</div>
											<div className="w-16 text-gray-600 truncate">
												Head Prod
											</div>
											<div className="flex-1 text-gray-600 truncate">
												mike@cloudstart.com
											</div>
											<div className="w-16 text-center">
												<span className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs">
													📧
												</span>
											</div>
										</div>
									</div>

									{/* Row 4 - Processing */}
									<div className="opacity-60">
										<div className="flex text-xs p-3 items-center">
											<div className="w-20">
												<div className="flex items-center space-x-1">
													<div className="w-2 h-2 border border-green-500 border-t-transparent rounded-full animate-spin"></div>
													<span className="text-green-600 text-xs">
														Adding...
													</span>
												</div>
											</div>
											<div className="w-20 text-gray-400">---</div>
											<div className="w-16 text-gray-400">---</div>
											<div className="flex-1 text-gray-400">---</div>
											<div className="w-16 text-center">
												<span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
													⏳
												</span>
											</div>
										</div>
									</div>
								</div>

								<div className="mt-3 flex items-center justify-between text-xs">
									<div className="text-gray-500">
										4 of 47 leads • Real-time enrichment
									</div>
									<div className="flex items-center space-x-2">
										<div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
										<span className="text-green-600 font-medium">
											Live updates
										</span>
									</div>
								</div>
							</div>
						</div>

						<div className="lg:order-2">
							<div className="flex items-center space-x-3 mb-6">
								<div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
									<Mail className="w-6 h-6 text-green-600" />
								</div>
								<h3 className="text-2xl font-bold text-gray-900">
									Real-time Contact Finding & Verification
								</h3>
							</div>
							<p className="text-lg text-gray-600 mb-6">
								While others use stale databases, we find and verify contacts in
								real-time. No more bounced emails or spam folder destinations.
							</p>
							<ul className="space-y-3 text-gray-600">
								<li className="flex items-center space-x-3">
									<CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
									<span>Live email verification (not cached data)</span>
								</li>
								<li className="flex items-center space-x-3">
									<CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
									<span>Phone numbers & LinkedIn profiles</span>
								</li>
								<li className="flex items-center space-x-3">
									<CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
									<span>Company size, funding, tech stack</span>
								</li>
								<li className="flex items-center space-x-3">
									<CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
									<span>Deliverability scoring & spam prevention</span>
								</li>
							</ul>
						</div>
					</div>
				</div>

				{/* Solution 3: Seamless Integration */}
				<div>
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
						<div>
							<div className="flex items-center space-x-3 mb-6">
								<div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
									<Zap className="w-6 h-6 text-purple-600" />
								</div>
								<h3 className="text-2xl font-bold text-gray-900">
									Seamless API Integration
								</h3>
							</div>
							<p className="text-lg text-gray-600 mb-6">
								No more CSV exports and manual imports. Direct API integration with
								your outreach tools means leads flow automatically from research to
								outreach.
							</p>
							<ul className="space-y-3 text-gray-600">
								<li className="flex items-center space-x-3">
									<CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
									<span>Direct Instantly.ai integration</span>
								</li>
								<li className="flex items-center space-x-3">
									<CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
									<span>Smartlead API connection</span>
								</li>
								<li className="flex items-center space-x-3">
									<CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
									<span>Clay API integration</span>
								</li>
								<li className="flex items-center space-x-3">
									<CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
									<span>Custom webhook support</span>
								</li>
							</ul>
						</div>

						{/* Integration Mockup */}
						<div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-3xl">
							<div className="bg-white rounded-2xl p-6 shadow-sm">
								<div className="flex items-center space-x-3 mb-4">
									<div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
										<Zap className="w-4 h-4 text-white" />
									</div>
									<span className="font-medium text-gray-900">
										Integration Hub
									</span>
								</div>
								<div className="space-y-4">
									<div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
										<div className="flex items-center space-x-3">
											<Image
												src="/instantly.webp"
												alt="Instantly logo"
												width={32}
												height={32}
												className="rounded"
											/>
											<span className="font-medium">Instantly.ai</span>
										</div>
										<span className="text-green-600 text-sm">✓ Connected</span>
									</div>
									<div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
										<div className="flex items-center space-x-3">
											<Image
												src="/smartlead.webp"
												alt="Smartlead logo"
												width={32}
												height={32}
												className="rounded"
											/>
											<span className="font-medium">Smartlead</span>
										</div>
										<span className="text-green-600 text-sm">✓ Connected</span>
									</div>
									<div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
										<div className="flex items-center space-x-3">
											<Image
												src="/clay.webp"
												alt="Clay logo"
												width={32}
												height={32}
												className="rounded"
											/>
											<span className="font-medium">Clay</span>
										</div>
										<span className="text-green-600 text-sm">✓ Connected</span>
									</div>
									<div className="text-center p-3 border-2 border-dashed border-gray-200 rounded-lg text-gray-500 text-sm">
										+ Add Custom Integration
									</div>
									<div className="bg-green-50 p-3 rounded-lg text-center">
										<p className="text-sm text-green-700">
											🚀 47 leads automatically exported to Instantly campaign
											"React CTOs Q1 2025"
										</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
